import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';

export interface AddressFields {
  hasState: boolean;
  hasPostal: boolean;
  stateRequired: boolean;
  postalRequired: boolean;
  cityRequired: boolean;
  addressRequired: boolean;
  /** Uppercase text map key, e.g. 'STATE', 'PROVINCE', 'COUNTY' */
  stateLabelKey: string;
  /** Uppercase text map key, e.g. 'POSTAL_CODE', 'ZIP', 'PIN_CODE' */
  postalLabelKey: string;
  /** Uppercase text map key, e.g. 'CITY', 'POST_TOWN', 'DISTRICT' */
  cityLabelKey: string;
  /** Pre-loaded state/province options when sub_keys are present */
  stateOptions?: { key: string; label: string }[];
}

export const DEFAULT_ADDRESS_FIELDS: AddressFields = {
  hasState: true,
  hasPostal: true,
  stateRequired: false,
  postalRequired: false,
  cityRequired: true,
  addressRequired: false,
  stateLabelKey: 'STATE',
  postalLabelKey: 'POSTAL_CODE',
  cityLabelKey: 'CITY',
};

// https://github.com/google/libaddressinput/wiki/AddressValidationMetadata
// Format string field codes: N=name O=org A=address D=dependent-locality
//   C=city/locality S=state/province Z=postal X=sorting-code
// Type values from the metadata JSON are uppercased directly to produce text map keys.
const S3_BASE = 'https://prweb-assets-prod.s3.us-east-1.amazonaws.com/ssl-address';

@Injectable({ providedIn: 'root' })
export class AddressMetadataService {
  /** Async shareReplay cache keyed by uppercase country code */
  private obsCache = new Map<string, Observable<AddressFields>>();
  /** Synchronous cache populated as each fetch completes */
  private syncCache = new Map<string, AddressFields>();

  constructor(private http: HttpClient) {}

  /**
   * Returns an Observable of AddressFields for the given ISO country code.
   * Results are cached; errors fall back to DEFAULT_ADDRESS_FIELDS.
   */
  getFields(countryCode: string): Observable<AddressFields> {
    if (!countryCode) return of(DEFAULT_ADDRESS_FIELDS);
    const code = countryCode.toUpperCase();
    if (!this.obsCache.has(code)) {
      const obs = this.http.get<any>(`${S3_BASE}/${code}`).pipe(
        map(data => this.parse(data)),
        tap(fields => this.syncCache.set(code, fields)),
        catchError(() => of(DEFAULT_ADDRESS_FIELDS)),
        shareReplay(1),
      );
      this.obsCache.set(code, obs);
    }
    return this.obsCache.get(code);
  }

  /**
   * Synchronous cache read. Returns DEFAULT_ADDRESS_FIELDS if not yet loaded.
   * Call getFields() first to trigger the async load and populate the cache.
   */
  getCached(countryCode: string): AddressFields {
    if (!countryCode) return DEFAULT_ADDRESS_FIELDS;
    return this.syncCache.get(countryCode.toUpperCase()) ?? DEFAULT_ADDRESS_FIELDS;
  }

  private parse(data: any): AddressFields {
    const fmt: string = data.fmt || '%N%n%O%n%A%n%C';
    const require: string = data.require || 'AC';

    const hasState = fmt.includes('%S');
    const hasPostal = fmt.includes('%Z');

    const stateRequired = require.includes('S');
    const postalRequired = require.includes('Z');
    const cityRequired = require.includes('C');
    const addressRequired = require.includes('A');

    const postalLabelKey = (data.zip_name_type || 'postal_code').toUpperCase();
    const stateLabelKey  = (data.state_name_type || 'state').toUpperCase();
    const cityLabelKey   = (data.locality_name_type || 'city').toUpperCase();

    let stateOptions: { key: string; label: string }[] | undefined;
    if (hasState && data.sub_keys) {
      const keys: string[] = data.sub_keys.split('~');
      const names: string[] = data.sub_names ? data.sub_names.split('~') : keys;
      stateOptions = keys.map((k, i) => ({ key: k, label: names[i] || k }));
    }

    return { hasState, hasPostal, stateRequired, postalRequired, cityRequired, addressRequired, stateLabelKey, postalLabelKey, cityLabelKey, stateOptions };
  }
}
